import { Header } from "components/DesignSystem/Header/Header";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { RuleEngine } from "components/RuleEngine/RuleEngine";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useEffect, useState } from "react";
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { Transaction, TxnAccountType } from "types/Models/books";
import { Transactions } from "../../../components/Transaction/Transactions";
import { ActionRequiredPopOver } from "components/ActionRequiredPopover/ActionRequiredPopover";
import { useActionItems } from "hooks/useActionItems";
import { useAppDispatch } from "hooks/useAppDispatch";
import { resetTxnFilters } from "store/slices/transactionFilter";
import { parse, stringify } from "qs";
import { EmptyScreen } from "../EmptyScreen";
import { Button } from "components/DesignSystem/Button/Button";
import { Stripe } from "components/icons/Logos/Stripe";
import { GradientArrow } from "components/icons/GradientArrow";
import { Inkle } from "components/icons/Logos/Inkle";
import { useGetAllConnections } from "hooks/useGetAllConnections";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { useBankConnect } from "hooks/useBankConnect";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { setOpenComment } from "store/slices/openComment";
import { TransactionActions } from "components/Transaction/TransactionActions";
import { TransactionsSidePanel } from "components/Transaction/TransactionsSidePanel";
import { useGetAllTransactionsDataQuery } from "store/apis/transactions";
import { useLocalStorage } from "hooks/useLocalStorage";
import { ShowControl } from "hooks/useShow";
import { SideChatControl } from "components/SideChatControl/SideChatControl";
import { useDisableContentTopPadding } from "hooks/useDisableContentTopPadding";
import { useAppSelector } from "hooks/useAppSelector";
import { useUpdateQuery } from "hooks/useQuery";
import { NoteResponse } from "store/apis/notes";
import { setTransactionsInChat } from "store/slices/transactionsInChat";
import { useAnalytics } from "hooks/useAnalytics";
import { useDrop } from "react-dnd";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";

const TxnTable = ({
  txnAccountType,
  sideChat,
}: {
  txnAccountType?: TxnAccountType;
  sideChat: ShowControl;
}) => {
  return (
    <div className="t-flex t-h-full">
      <div className="t-h-full t-grow t-pb-5 t-overflow-x-hidden scrollbar-visible t-pr-5">
        <Transactions txnAccountType={txnAccountType} sideChat={sideChat} />
      </div>
    </div>
  );
};

const StripeTable = ({ sideChat }: { sideChat: ShowControl }) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const { isCpa, isForeignCA } = useRoleBasedView();

  const { onConnect, isLoading: isConnectBankLoading } = useBankConnect();

  const { stripeConnection, isLoading, isSuccess } = useGetAllConnections({
    groupId,
    entityId,
  });

  const isEmpty = stripeConnection?.bank_accounts?.length === 0;

  return (
    <Async.Root isLoading={isLoading} isSuccess={isSuccess} isEmpty={isEmpty}>
      <Async.Empty>
        <EmptyScreen
          className="t-h-[70dvh]"
          text="Inkle Books now natively integrates with Stripe"
          cta={
            <Button
              size="small"
              customType="primary"
              isLoading={isConnectBankLoading}
              disabled={isConnectBankLoading}
              onClick={() =>
                onConnect({
                  connectionId: stripeConnection?.uuid!,
                  invokedFrom:
                    isCpa || isForeignCA
                      ? `/books/transactions/stripe?company=${groupId}&entity=${entityId}`
                      : `/books/transactions/stripe?entity=${entityId}`,
                })
              }
            >
              Connect
            </Button>
          }
        >
          <div className="t-flex t-gap-5 t-items-center">
            <Stripe />
            <GradientArrow />
            <Inkle />
          </div>
        </EmptyScreen>
      </Async.Empty>
      <Async.Success>
        <div className="t-flex t-h-full">
          <div className="t-h-full t-grow t-pb-5 t-overflow-x-hidden scrollbar-visible t-pr-5">
            <TxnTable txnAccountType="STRIPE" sideChat={sideChat} />
          </div>
        </div>
      </Async.Success>
    </Async.Root>
  );
};

export const TransactionsPage = () => {
  useDisableContentTopPadding();

  const { localData, setItem: updateSideChatState } = useLocalStorage(
    "transactionSideChat",
    true
  );
  const isSmallScreen = useIsSmallScreen();
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false);

  const isSideChatShown = isSmallScreen ? isMobileChatOpen : localData;

  const setSideChatShown = (shown: boolean) => {
    if (isSmallScreen) {
      setIsMobileChatOpen(shown);
      return;
    }
    updateSideChatState(shown);
  };

  const sideChat = {
    shown: isSideChatShown,
    toggle: () => setSideChatShown(!isSideChatShown),
    show: () => setSideChatShown(true),
    hide: () => setSideChatShown(false),
  };
  const { uuid: groupId } = useCurrentGroupContext();
  const dispatch = useAppDispatch();
  const { path, url } = useRouteMatch();
  const history = useHistory();
  const { pathname, search } = useLocation();
  let query = parse(search, { ignoreQueryPrefix: true });
  const openCommentParam = query.open_comment as string | undefined;

  const searchQuery = stringify(
    {
      company: query.company || null,
      entity: query.entity || null,
    },
    { skipNulls: true, addQueryPrefix: true }
  );

  const { action_items_count, isBankReconnectRequired } = useActionItems();

  const isRuleEnginePage = pathname.includes("/rules");
  const isStripePage = pathname.includes("/stripe");

  const resetFilters = () => {
    dispatch(resetTxnFilters());
  };

  const entityId = useCurrentEntityId();
  const openComment = useAppSelector((state) => state.openComment.openComment);

  const { update } = useUpdateQuery();

  const allFilters = useAppSelector(
    (state) => state.transactionCardPreview.filtersApplied
  );

  const setActiveComment = (comment: NoteResponse) => {
    if (comment.transaction?.uuid) {
      update({
        query: "selected_transaction_id",
        value: comment.transaction?.uuid,
      });
    }

    if (comment.transaction) {
      dispatch(setOpenComment(comment.transaction.uuid));
    }
  };

  const onDrop = async (transaction: string) => {
    const txn: Transaction = JSON.parse(transaction);
    const transactionsToSendtoChat = {
      id: txn.uuid,
      amount: txn.amount,
      source: txn.source,
      date: txn.date,
      vendor: {
        logo: txn.logo,
        name: txn.merchant,
      },
      isCreditCard: txn.is_credit_card,
    };
    dispatch(
      setTransactionsInChat({
        transactionsInChat: [transactionsToSendtoChat],
        entityId: entityId,
      })
    );
    sideChat.show();
  };

  const { trackEvent } = useAnalytics();

  const [{ isOver }, drop] = useDrop(() => ({
    accept: "transaction",
    drop: ({ transaction }: { transaction: string }) => onDrop(transaction),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const ref = (node: HTMLDivElement | null) => {
    drop(node);
  };

  const { data: transactionData } = useGetAllTransactionsDataQuery(
    {
      entityId,
      groupId,
    },
    {
      skip: !groupId || !entityId,
      refetchOnMountOrArgChange: true,
    }
  );

  const { channel_url = "" } = transactionData || {};
  const [channelId, setChannelId] = useState<string | null>(null);

  useEffect(() => {
    setChannelId(channel_url);
  }, [channel_url]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (openCommentParam) {
      dispatch(setOpenComment(openCommentParam));
      const currentSearch = parse(search, { ignoreQueryPrefix: true });
      delete currentSearch.open_comment;
      timeout = setTimeout(() => {
        history.replace(
          `${pathname}${stringify(currentSearch, { addQueryPrefix: true })}`
        );
      }, 5000);
    }
    return () => clearTimeout(timeout);
  }, [openCommentParam]);

  return (
    <div className="t-flex t-h-full t-overflow-x-hidden">
      <DashboardContainer className="t-h-full t-grow-0 t-shrink t-overflow-x-auto t-relative">
        <DashboardContainer.Header className="t-z-header">
          <Header
            v2
            right={
              isRuleEnginePage ? (
                <>
                  {!sideChat.shown && (
                    <div className="t-flex t-items-center t-gap-3 t-ml-3 sm:-t-mr-2">
                      <span className="t-bg-neutral-20 t-h-2/3 t-w-[1.5px]"></span>
                      <SideChatControl
                        channelId={channelId ? channelId : channel_url}
                        open={sideChat.shown}
                        onOpenChange={sideChat.toggle}
                      />
                    </div>
                  )}
                </>
              ) : (
                <TransactionActions
                  sideChat={sideChat}
                  channelId={channelId ?? channel_url}
                  isStripePage={isStripePage}
                />
              )
            }
            title="Transactions"
          />
        </DashboardContainer.Header>
        <DashboardContainer.Content className="t-flex t-flex-col t-min-h-0 t-pt-5 !t-overflow-hidden">
          {!isRuleEnginePage && (
            <div className="t-shrink-0 t-px-5">
              <ToggleGroup
                type="single"
                size="small"
                value={isStripePage ? "stripe" : "banks"}
                onValueChange={(value) => {
                  resetFilters();
                  history.push(
                    value === "stripe"
                      ? `${url}/stripe${searchQuery}`
                      : `${url}${searchQuery}`
                  );
                }}
              >
                <ToggleGroupItem
                  value="banks"
                  className="t-flex t-items-center t-gap-1 t-whitespace-nowrap"
                >
                  Banks & Cards
                  {action_items_count > 0 && isBankReconnectRequired && (
                    <ActionRequiredPopOver />
                  )}
                </ToggleGroupItem>
                <ToggleGroupItem value="stripe">Stripe</ToggleGroupItem>
              </ToggleGroup>
            </div>
          )}
          <div className="t-min-h-0 t-flex-1">
            <Switch>
              <Route exact path={path}>
                <TxnTable txnAccountType="EXCLUDE_STRIPE" sideChat={sideChat} />
              </Route>
              <Route exact path={`${path}/stripe`}>
                <StripeTable sideChat={sideChat} />
              </Route>
              <Route exact path={`${path}/rules`}>
                <RuleEngine />
              </Route>
            </Switch>
          </div>
        </DashboardContainer.Content>
      </DashboardContainer>

      <TransactionsSidePanel
        open={sideChat.shown}
        onToggle={sideChat.toggle}
        channelId={channelId}
        setChannelId={setChannelId}
        filter={groupId ? { group_uuid: { $in: [groupId] } } : {}}
      />
    </div>
  );
};
